import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, CheckCircle2, MessageCircle } from 'lucide-react';
import VideoSection from '../shared/VideoSection';
import NativeVideoPlayer from '../shared/NativeVideoPlayer';
import InvestmentSummaryForm from '../forms/InvestmentSummaryForm';
import BrochureDownload from './BrochureDownload';
import { CALCULATOR_ANCHOR } from './GroupAffordabilityCalculator';
import { useFamilyPtyCms } from '../../hooks/useFamilyPtyCms';
import { scrollToAnchor } from '../../utils/scrollToAnchor';

export const FAMILY_PTY_FORM_ANCHOR = 'family-form';

interface Props {
  /**
   * Where the thank-you panel's "Check what your group can afford" goes. On
   * /family-pty the calculator is on the page (scroll); elsewhere (the home
   * page) it links to the calculator's own URL.
   */
  calculatorHref?: string;
}

/**
 * The Group Millionaire Strategy (Family Pty) "video | form" block: CMS video
 * heading + video + brochure button on the left, the 4-field family_pty
 * InvestmentSummaryForm on the right. Shared by /family-pty and the home page
 * (Jaco 2026-10-12) so both read the same Website CMS → Family Pty content.
 * The thank-you panel replaces the form in place (no redirect).
 */
export default function FamilyPtyVideoForm({ calculatorHref }: Props) {
  const { content, loading } = useFamilyPtyCms();
  const { video, formSection, thankYou } = content;
  const [submitted, setSubmitted] = useState(false);

  const handleSubmitted = () => {
    setSubmitted(true);
    window.setTimeout(() => scrollToAnchor(FAMILY_PTY_FORM_ANCHOR), 50);
  };

  const entry = video.entry;
  const mp4Src = entry.type === 'upload' ? entry.uploadedUrl || '' : '';
  const youtubeId = entry.type === 'youtube' ? entry.youtubeId || '' : '';

  const calculatorButtonClass =
    'inline-flex items-center gap-2 border-2 border-primary text-primary hover:bg-primary hover:text-white font-semibold px-6 py-3 rounded-full transition-colors';
  const calculatorButtonContent = (
    <>
      <Calculator className="w-5 h-5" aria-hidden="true" /> Check what your group can afford
    </>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
      {video.enabled && (
        <div>
          {video.heading && <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">{video.heading}</h2>}
          {/* Wait for the CMS before choosing a player, so the code-default
              video never flashes in before the uploaded one. */}
          {loading ? (
            <div className="aspect-video w-full rounded-xl bg-gray-200 animate-pulse" />
          ) : mp4Src ? (
            <NativeVideoPlayer src={mp4Src} poster={entry.thumbnailUrl} />
          ) : (
            <VideoSection videoId={youtubeId || undefined} />
          )}
          <div className="mt-5 flex justify-center">
            <BrochureDownload source="video" label="Download Brochure" />
          </div>
        </div>
      )}
      {formSection.enabled && (
        <div
          id={FAMILY_PTY_FORM_ANCHOR}
          className={`scroll-mt-16 ${video.enabled ? '' : 'lg:col-span-2 max-w-xl mx-auto w-full'}`}
        >
          {submitted ? (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-6 md:p-8 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">{thankYou.heading}</h2>
              <p className="mt-3 text-gray-600">{thankYou.message}</p>
              <p className="mt-3 inline-flex items-center gap-2 text-sm text-gray-500">
                <MessageCircle className="w-4 h-4 text-[#25D366]" /> Delivered via WhatsApp
              </p>
              <div className="mt-6 flex flex-col items-center gap-3">
                <BrochureDownload source="thank_you" />
                {calculatorHref ? (
                  <Link to={calculatorHref} className={calculatorButtonClass}>
                    {calculatorButtonContent}
                  </Link>
                ) : (
                  <button type="button" onClick={() => scrollToAnchor(CALCULATOR_ANCHOR)} className={calculatorButtonClass}>
                    {calculatorButtonContent}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-2xl border border-gray-100 p-5 md:p-7">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">{formSection.heading}</h2>
              {formSection.subtext && <p className="mt-1 mb-4 text-sm text-gray-500">{formSection.subtext}</p>}
              <InvestmentSummaryForm
                formContext="family_pty"
                submitLabel="Send Investment Summary and Info"
                whatsappNote={formSection.whatsappNote}
                onSubmitted={handleSubmitted}
              />
              {formSection.disclaimer && <p className="mt-4 text-xs text-gray-400 leading-relaxed">{formSection.disclaimer}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
